import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { apiGet, apiPut } from '../utils/api';
import './StudentOnboardingWizard.css';
import {
  Close as CloseIcon,
  Person as PersonIcon,
  School as SchoolIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  CalendarToday as CalendarIcon,
  MenuBook as ReadingIcon,
  Groups as CultureIcon,
  Notes as NotesIcon,
  ArrowForward as ArrowForwardIcon,
  ArrowBack as ArrowBackIcon,
  CheckCircle as CheckCircleIcon,
  AutoAwesome as WizardIcon
} from '@mui/icons-material';

// Use any type to avoid interface conflicts
type Student = any;

interface Culture {
  id: number;
  code: string;
  name: string;
  description: string | null;
}

interface StudentOnboardingWizardProps {
  isOpen: boolean;
  onClose: () => void;
  student: Student | null;
  onStudentUpdated: (student: Student) => void;
}

interface WizardFormData {
  reading_level: string;
  grade_level: string;
  parent_email: string;
  phone: string;
  culture_ids: number[];
  notes: string;
}

const StudentOnboardingWizard: React.FC<StudentOnboardingWizardProps> = ({
  isOpen,
  onClose,
  student,
  onStudentUpdated
}) => {
  const { t } = useTranslation();
  const { token } = useAuth();
  const [currentStep, setCurrentStep] = useState(1);
  const [cultures, setCultures] = useState<Culture[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);
  const [formData, setFormData] = useState<WizardFormData>({
    reading_level: '',
    grade_level: '',
    parent_email: '',
    phone: '',
    culture_ids: [],
    notes: ''
  });

  const totalSteps = 5;

  // Fetch cultures when modal opens
  useEffect(() => {
    if (isOpen && token) {
      fetchCultures();
    }
  }, [isOpen, token]);

  // Pre-populate form data when student changes
  useEffect(() => {
    if (student) {
      setFormData({
        reading_level: student.reading_level ? student.reading_level.toString() : '',
        grade_level: student.grade_level || '',
        parent_email: student.parent_email || '',
        phone: student.phone || '',
        culture_ids: [], // Will be populated after cultures are loaded
        notes: student.notes || ''
      });
    }
  }, [student]);

  // Update culture_ids when cultures are loaded
  useEffect(() => {
    if (student && cultures.length > 0) {
      const studentCultureIds = student.culture_codes
        ? cultures.filter(c => student.culture_codes?.includes(c.code)).map(c => c.id)
        : [];
      
      setFormData(prev => ({
        ...prev,
        culture_ids: studentCultureIds
      }));
    }
  }, [student, cultures]);

  const fetchCultures = async () => {
    try {
      const response = await apiGet('/api/students/cultures', token);
      if (response.ok) {
        const data = await response.json();
        setCultures(data.cultures || []);
      }
    } catch (error) {
      console.error('Failed to fetch cultures:', error);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear errors when user starts typing
    if (errors.length > 0) {
      setErrors([]);
    }
  };

  const handleCultureToggle = (cultureId: number) => {
    setFormData(prev => ({
      ...prev,
      culture_ids: prev.culture_ids.includes(cultureId)
        ? prev.culture_ids.filter(id => id !== cultureId)
        : [...prev.culture_ids, cultureId]
    }));
  };

  const validateCurrentStep = (): boolean => {
    const newErrors: string[] = [];

    switch (currentStep) {
      case 1: // Reading Level
        if (formData.reading_level && (isNaN(Number(formData.reading_level)) || Number(formData.reading_level) < 0)) {
          newErrors.push('Reading level must be a positive number');
        }
        break;
      case 2: // Grade Level
        // Grade level is optional, no validation needed
        break;
      case 3: // Contact Info
        if (formData.parent_email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.parent_email)) {
          newErrors.push('Invalid parent email format');
        }
        break;
      case 4: // Culture Settings
        // Culture settings are optional, no validation needed
        break;
      case 5: // Notes
        // Notes are optional, no validation needed
        break;
    }

    setErrors(newErrors);
    return newErrors.length === 0;
  };

  const handleNext = () => {
    if (validateCurrentStep() && currentStep < totalSteps) {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleSave = async () => {
    if (!student) return;

    setSaving(true);
    setErrors([]);

    try {
      // Prepare submission data - include all existing student data plus updates
      const submissionData = {
        // Include required fields from existing student data
        first_name: student.first_name,
        last_name: student.last_name,
        // Include other existing fields that might be needed
        date_of_birth: student.date_of_birth || null,
        student_id: student.student_id || null,
        email: student.email || null,
        // Include the wizard form updates
        reading_level: formData.reading_level ? parseInt(formData.reading_level) : null,
        grade_level: formData.grade_level || null,
        parent_email: formData.parent_email || null,
        phone: formData.phone || null,
        notes: formData.notes || null,
        culture_ids: formData.culture_ids.length > 0 ? formData.culture_ids : []
      };

      const response = await apiPut(`/api/students/${student.id}`, submissionData, token);
      const result = await response.json();

      if (response.ok) {
        // Success - update student in parent component
        onStudentUpdated(result.student);
        onClose();
      } else {
        // Handle errors
        if (result.error) {
          setErrors([result.error]);
        } else {
          setErrors(['Failed to update student']);
        }
      }
    } catch (error) {
      setErrors(['Network error. Please try again.']);
      console.error('Error updating student:', error);
    } finally {
      setSaving(false);
    }
  };

  const getStepTitle = (step: number): string => {
    switch (step) {
      case 1: return t('classWork.readingLevel');
      case 2: return "Grade Level";
      case 3: return "Contact Information";
      case 4: return "Cultural Background";
      case 5: return t('modals.additionalNotes');
      default: return "";
    }
  };

  const getStepDescription = (step: number): string => {
    switch (step) {
      case 1: return "What's their current reading level? This helps us tailor assignments.";
      case 2: return "What grade are they in? This helps with age-appropriate content.";
      case 3: return "How can parents or guardians be reached?";
      case 4: return "What cultural backgrounds should we consider for personalized learning?";
      case 5: return "Any special notes or information about this student?";
      default: return "";
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1: // Reading Level
        return (
          <div className="wizard-step-content">
            <div className="step-input-group">
              <label className="step-label">
                <ReadingIcon className="step-icon" />
                {t('classWork.readingLevel')}
              </label>
              <input
                type="number"
                name="reading_level"
                value={formData.reading_level}
                onChange={handleInputChange}
                className="step-input"
                min="0"
                placeholder="Enter Lexile reading level (e.g. 300, 450)"
                disabled={saving}
              />
              <p className="step-help">
                Enter a Lexile reading level (e.g. 300, 450). This helps us create assignments at the right difficulty level.
              </p>
            </div>
          </div>
        );

      case 2: // Grade Level
        return (
          <div className="wizard-step-content">
            <div className="step-input-group">
              <label className="step-label">
                <SchoolIcon className="step-icon" />
                Grade Level
              </label>
              <select
                name="grade_level"
                value={formData.grade_level}
                onChange={handleInputChange}
                className="step-select"
                disabled={saving}
              >
                <option value="">Select Grade Level</option>
                <option value="K">Kindergarten</option>
                <option value="1st">1st Grade</option>
                <option value="2nd">2nd Grade</option>
                <option value="3rd">3rd Grade</option>
                <option value="4th">4th Grade</option>
                <option value="5th">5th Grade</option>
                <option value="6th">6th Grade</option>
                <option value="7th">7th Grade</option>
                <option value="8th">8th Grade</option>
                <option value="9th">9th Grade</option>
                <option value="10th">10th Grade</option>
                <option value="11th">11th Grade</option>
                <option value="12th">12th Grade</option>
              </select>
              <p className="step-help">
                This helps us ensure content is age-appropriate and engaging.
              </p>
            </div>
          </div>
        );

      case 3: // Contact Information
        return (
          <div className="wizard-step-content">
            <div className="step-input-group">
              <label className="step-label">
                <EmailIcon className="step-icon" />
                Parent/Guardian Email
              </label>
              <input
                type="email"
                name="parent_email"
                value={formData.parent_email}
                onChange={handleInputChange}
                className="step-input"
                placeholder="parent@example.com"
                disabled={saving}
              />
              <p className="step-help">
                We'll use this to keep parents informed about their child's progress.
              </p>
            </div>
            
            <div className="step-input-group">
              <label className="step-label">
                <PhoneIcon className="step-icon" />
                Phone Number
              </label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                className="step-input"
                placeholder="+1 (555) 123-4567"
                disabled={saving}
              />
              <p className="step-help">
                Optional: For urgent communication if needed.
              </p>
            </div>
          </div>
        );

      case 4: // Culture Settings
        return (
          <div className="wizard-step-content">
            <div className="step-input-group">
              <label className="step-label">
                <CultureIcon className="step-icon" />
                Cultural Background
              </label>
              <p className="step-description">
                Select cultural backgrounds that may influence this student's learning experience:
              </p>
              <div className="culture-selection">
                {cultures.map((culture) => (
                  <div key={culture.id} className="culture-item">
                    <label className="culture-checkbox">
                      <input
                        type="checkbox"
                        checked={formData.culture_ids.includes(culture.id)}
                        onChange={() => handleCultureToggle(culture.id)}
                        disabled={saving}
                      />
                      <span className="culture-name">{culture.name}</span>
                    </label>
                  </div>
                ))}
              </div>
              <p className="step-help">
                This helps us create culturally relevant and engaging assignments.
              </p>
            </div>
          </div>
        );

      case 5: // Notes
        return (
          <div className="wizard-step-content">
            <div className="step-input-group">
              <label className="step-label">
                <NotesIcon className="step-icon" />
                {t('modals.additionalNotes')}
              </label>
              <textarea
                name="notes"
                value={formData.notes}
                onChange={handleInputChange}
                className="step-textarea"
                placeholder="Any special information about this student..."
                rows={4}
                disabled={saving}
              />
              <p className="step-help">
                Any additional information that might help with their learning experience.
              </p>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  if (!isOpen || !student) {
    return null;
  }

  return (
    <div className="wizard-overlay">
      <div className="wizard-container">
        {/* Header */}
        <div className="wizard-header">
          <div className="wizard-title-section">
            <div className="wizard-icon">
              <WizardIcon />
            </div>
            <div>
              <h2 className="wizard-title">Student Onboarding</h2>
              <p className="wizard-subtitle">
                Let's set up <strong>{student.full_name}</strong> for success!
              </p>
            </div>
          </div>
          <button 
            className="wizard-close-btn" 
            onClick={onClose}
            disabled={saving}
          >
            <CloseIcon />
          </button>
        </div>

        {/* Progress Bar */}
        <div className="wizard-progress">
          <div className="progress-bar">
            <div 
              className="progress-fill" 
              style={{ width: `${(currentStep / totalSteps) * 100}%` }}
            />
          </div>
          <div className="progress-text">
            Step {currentStep} of {totalSteps}
          </div>
        </div>

        {/* Step Content */}
        <div className="wizard-content">
          <div className="step-header">
            <h3 className="step-title">{getStepTitle(currentStep)}</h3>
            <p className="step-description">{getStepDescription(currentStep)}</p>
          </div>

          {/* Error Messages */}
          {errors.length > 0 && (
            <div className="wizard-errors">
              {errors.map((error, index) => (
                <div key={index} className="wizard-error">
                  {error}
                </div>
              ))}
            </div>
          )}

          {renderStepContent()}
        </div>

        {/* Navigation */}
        <div className="wizard-navigation">
          <button
            type="button"
            onClick={handlePrevious}
            disabled={currentStep === 1 || saving}
            className="wizard-btn wizard-btn-secondary"
          >
            <ArrowBackIcon />
            Previous
          </button>

          <div className="wizard-step-indicators">
            {Array.from({ length: totalSteps }, (_, i) => i + 1).map((step) => (
              <div
                key={step}
                className={`step-indicator ${step <= currentStep ? 'active' : ''} ${step < currentStep ? 'completed' : ''}`}
              >
                {step < currentStep ? <CheckCircleIcon /> : step}
              </div>
            ))}
          </div>

          {currentStep < totalSteps ? (
            <button
              type="button"
              onClick={handleNext}
              disabled={saving}
              className="wizard-btn wizard-btn-primary"
            >
              Next
              <ArrowForwardIcon />
            </button>
          ) : (
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              className="wizard-btn wizard-btn-primary"
            >
              {saving ? 'Saving...' : 'Complete Setup'}
              <CheckCircleIcon />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default StudentOnboardingWizard;
